/* eslint-disable no-unused-vars */
/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-accordion.html";
import { FlattenedNodesObserver } from "@polymer/polymer/lib/utils/flattened-nodes-observer.js";

/**
 * <p>A Nuskin accordion that accepts `ns-accordion-item` in its slot or an array or objects .</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/279861734/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/291557363/1/latest/LCYLlE5UvRv1hPLlbltPXj0be9rPrIuv4WVHfEbG9KYZUsXZvYMyA36gaMkeR60S9D2QkoKCn8dWK3aWJ1vfHtQlE/Accordion-2x.png" alt="Accordion Item UX Documentation">
 * </a>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <h3>Accordion that only allows one item to be open at a time and has some sub items.</h3>
 *     <ns-accordion single-open>
 *       <ns-accordion-item title="Has sub items">
 *         <ns-accordion-item level="second">sample content</ns-accordion-item>
 *         <ns-accordion-item level="second" title="another item">
 *           <p>
 *             <span>Lorem ipsum dolor sit amet consectetur adipisicing elit. Reiciendis obcaecati aut quam expedita quas voluptatum blanditiis laborum vel tempore debitis minima, illo odit natus ipsa totam maiores non necessitatibus quasi!</span>
 *             <span>Consequuntur rerum, nam tempore voluptatem consequatur accusantium necessitatibus veniam quia enim minima assumenda consectetur nobis nesciunt voluptas sapiente a, fuga esse minus fugiat, ex dignissimos. Libero eum velit eius autem.</span>
 *             <span>Ullam modi quaerat ipsum debitis illum. Ea veniam obcaecati laboriosam fugit a quia consequuntur, perspiciatis neque error pariatur, beatae provident totam in architecto rerum sunt assumenda quibusdam vel vitae ipsa?</span>
 *           </p>
 *         </ns-accordion-item>
 *       </ns-accordion-item>
 *       <ns-accordion-item>
 *         some stuff
 *       </ns-accordion-item>
 *       <ns-accordion-item>
 *         some stuff
 *       </ns-accordion-item>
 *       <ns-accordion-item show-completed title="Has Completed Indicator">
 *         some stuff
 *       </ns-accordion-item>
 *     </ns-accordion>
 *     <br>
 *
 *     <h3>Accordion with controls</h3>
 *     <ns-accordion show-controls>
 *       <ns-accordion-item title="Has controls that">
 *         <p>Other Content</p>
 *       </ns-accordion-item>
 *       <ns-accordion-item title="Open and close all items">
 *         <p>Other Content</p>
 *       </ns-accordion-item>
 *     </ns-accordion>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-accordion-item>` doesn't provide any variable or mixins for styling.
 *
 *      ### Slots
 *
 *      ##### `<ns-accordion>` provides the following slots for content:
 *
 *      Custom property                 | Description                               | Default
 *      ------------------------------- |---------------------------------------    |-------------------
 *      `controls`                      | Replaces the default icons for controls   | `check`
 *      `default`                       | Holds the ns-accordion-items              | `A dom-repeat template in that iterates over an array of objects passed in`
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsAccordion extends PolymerElement {
  constructor() {
    super();
  }

  static get is() {
    return "ns-accordion";
  }

  static get properties() {
    return {
      label: {
        type: String,
        value: "Accordion Label"
      },
      showControls: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      singleOpen: {
        type: Boolean,
        value: false
      },
      _distNodeList: {
        type: Array,
        value: () => {
          return [];
        }
      }
    };
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  /**
   * Listen for click events in order to handle open states of all items in the accordion
   */
  ready() {
    super.ready();
    this.addEventListener("accordionitemtoggled", (e) => {
      if (this.singleOpen) {
        this.closeOthers(e.target);
      }
    });
  }

  /**
   * When connected we need to observe the distributed children so we can filter out
   * non HTML nodes and process them.
   */
  connectedCallback() {
    super.connectedCallback();
    this._distNodesObserver = new FlattenedNodesObserver(this, (info) => {
      this._processNewNodes(
        info.addedNodes.filter((node) => {
          return node.nodeType === Node.ELEMENT_NODE;
        })
      );
    });
  }

  /**
   * For each node in the flattened node tree add it to the _distNodeList array for later use.
   * @param {*} nodeList
   */
  _processNewNodes(nodeList) {
    for (const key in nodeList) {
      if (nodeList.hasOwnProperty(key)) {
        const node = nodeList[key];
        this._distNodeList.push(node);
      }
    }
  }

  /**
   * Open all top level items in the accordion
   */
  openAll(e) {
    this._distNodeList.forEach((node) => {
      node.open();
    });
  }

  /**
   * Close all top level items in the accordion
   */
  closeAll(e) {
    this._distNodeList.forEach((node) => {
      node.close();
    });
  }

  /**
   * When opening a new item close other open items
   */
  closeOthers(target) {
    if (target.opened) {
      // this._distNodeList.forEach(node => {
      //     node.close();
      // });
      this._closePrevSibling(target);
      this._closeNextSibling(target);
    }
  }

  next() {
    for (let i = 0; i < this._distNodeList.length; i++) {
      let node = this._distNodeList[i];
      if (node.opened && node.nextElementSibling) {
        node.nextElementSibling.toggle();
        return true;
      }
    }
    return false;
  }

  prev() {
    for (let i = 0; i < this._distNodeList.length; i++) {
      let node = this._distNodeList[i];
      if (node.opened && node.previousElementSibling) {
        node.previousElementSibling.toggle();
        return true;
      }
    }
    return false;
  }

  /**
   * Recursively close elements following the given target
   */
  _closeNextSibling(element) {
    if (element.nextElementSibling != null) {
      if (element.nextElementSibling.close) element.nextElementSibling.close();
      this._closeNextSibling(element.nextElementSibling);
    }
  }

  /**
   * Recursively close elements proceeding the given target
   */
  _closePrevSibling(element) {
    if (element.previousElementSibling != null) {
      element.previousElementSibling.close();
      this._closePrevSibling(element.previousElementSibling);
    }
  }
}
customElements.define(NsAccordion.is, NsAccordion);
